'use client'

import { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel'
import { Badge } from '@/components/ui/badge'
import { LazyImage, LoadingIndicator, NoImagePlaceholder } from '@/components/partials'
import { cn, getResizedPictureUrl } from '@/lib/utils'
import 'react-photo-view/dist/react-photo-view.css'

type PropsType = {
  mainPicture?: any
  pictures?: any[]
  productCode: string
  productTitle: string
}

type GalleryImage = {
  id: string
  file: { path: string }
}

const ProductImageGallery = ({ mainPicture, pictures, productCode, productTitle }: PropsType) => {
  const [api, setApi] = useState<CarouselApi>()
  const [thumbApi, setThumbApi] = useState<CarouselApi>()
  const [activeIndex, setActiveIndex] = useState(0)
  const [canScrollPrevThumb, setCanScrollPrevThumb] = useState(false)
  const [canScrollNextThumb, setCanScrollNextThumb] = useState(false)
  const [mounted, setMounted] = useState(false)
  const scrollYRef = useRef(0)

  // Use layoutEffect to ensure PhotoProvider mounts after DOM is ready
  useLayoutEffect(() => {
    setMounted(true)
    return () => {
      setMounted(false)
    }
  }, [])

  const allImages: GalleryImage[] = useMemo(() => {
    const images: GalleryImage[] = []
    const seenPaths = new Set<string>()

    const pushImage = (id: string, path?: string | null) => {
      if (!path || seenPaths.has(path)) {
        return
      }
      seenPaths.add(path)
      images.push({ id, file: { path } })
    }

    if (mainPicture?.path) {
      pushImage('main', mainPicture.path)
    }

    if (pictures?.length) {
      for (const p of pictures) {
        const path = p?.file?.path ?? p?.path
        const id = p?.id ?? path
        pushImage(String(id), path)
      }
    }

    return images
  }, [mainPicture, pictures])

  const hasImages = allImages.length > 0

  const scrollTo = (index: number) => {
    if (!api) {
      return
    }
    api.scrollTo(index)
  }

  // Keep activeIndex in sync
  useEffect(() => {
    if (!api) {
      return
    }

    const onSelect = () => {
      const index = api.selectedScrollSnap()
      setActiveIndex(index)

      if (thumbApi) {
        thumbApi.scrollTo(index)
      }
    }

    onSelect()
    api.on('select', onSelect)
    api.on('reInit', onSelect)

    return () => {
      api.off('select', onSelect)
      api.off('reInit', onSelect)
    }
  }, [api, thumbApi])

  useEffect(() => {
    if (!thumbApi) {
      return
    }

    const updateScrollState = () => {
      setCanScrollPrevThumb(thumbApi.canScrollPrev())
      setCanScrollNextThumb(thumbApi.canScrollNext())
    }

    updateScrollState()
    thumbApi.on('select', updateScrollState)
    thumbApi.on('reInit', updateScrollState)

    return () => {
      thumbApi.off('select', updateScrollState)
      thumbApi.off('reInit', updateScrollState)
    }
  }, [thumbApi])

  if (!mounted) {
    // Render a same-sized placeholder (not null) so the gallery keeps its grid column and reserved
    // height on SSR/first paint — otherwise the details column would be auto-placed into the
    // gallery's cell and visibly shift right once the gallery mounts.
    return (
      <div className="w-full space-y-3 md:space-y-4">
        <div className="relative overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
          <div className="h-75 sm:h-100 md:h-125 lg:h-150" />
        </div>
      </div>
    )
  }

  return (
    <PhotoProvider
      maskOpacity={0.9}
      loadingElement={<LoadingIndicator />}
      loop={true}
      bannerVisible={true}
      onVisibleChange={visible => {
        if (visible) {
          scrollYRef.current = window.scrollY
        } else {
          requestAnimationFrame(() => window.scrollTo({ top: scrollYRef.current, behavior: 'instant' }))
        }
      }}>
      <div className="w-full space-y-3 md:space-y-4" style={{ maxWidth: '100%', minWidth: 0, overflowX: 'clip' }}>
        {/* Main carousel */}
        <div
          className="relative overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100"
          style={{ maxWidth: '100%', minWidth: 0 }}>
          <Carousel
            setApi={setApi}
            opts={{ align: 'start', loop: allImages.length > 1 }}
            className="w-full"
            style={{ maxWidth: '100%', minWidth: 0 }}>
            <CarouselContent className="h-75 sm:h-100 md:h-125 lg:h-150">
              {hasImages ? (
                allImages.map((img, idx) => (
                  <CarouselItem key={img.id} className="h-full">
                    <PhotoView src={getResizedPictureUrl(img.file.path, 1024, 768)}>
                      <div className="relative h-full w-full cursor-pointer">
                        <LazyImage
                          src={getResizedPictureUrl(img.file.path, 800, 600)}
                          alt={`${productCode} / ${productTitle} ${idx + 1}`}
                          width={800}
                          height={600}
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 600px"
                          className="h-full w-full object-contain p-2 md:p-3"
                          priority={idx === 0}
                        />
                      </div>
                    </PhotoView>
                  </CarouselItem>
                ))
              ) : (
                <CarouselItem className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <NoImagePlaceholder />
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>

            {allImages.length > 1 && (
              <>
                <CarouselPrevious className="bg-background/80 hover:bg-brand left-2! hover:text-white" />
                <CarouselNext className="bg-background/80 hover:bg-brand right-2! hover:text-white" />
              </>
            )}
          </Carousel>

          {hasImages && allImages.length > 1 && (
            <Badge className="absolute right-3 bottom-3 z-10">
              {activeIndex + 1} / {allImages.length}
            </Badge>
          )}
        </div>

        {/* Thumbnails */}
        {allImages.length > 1 && (
          <div style={{ maxWidth: '100%', minWidth: 0, overflowX: 'clip', position: 'relative' }}>
            <Carousel
              setApi={setThumbApi}
              opts={{ align: 'start', dragFree: true, containScroll: 'trimSnaps' }}
              style={{ maxWidth: '100%', minWidth: 0 }}>
              <CarouselContent style={{ marginLeft: 0, maxWidth: '100%', minWidth: 0 }} className="gap-2">
                {allImages.map((img, idx) => (
                  <CarouselItem
                    key={img.id}
                    style={{ paddingLeft: 0, maxWidth: '100%', minWidth: 0 }}
                    className="basis-auto">
                    <button
                      type="button"
                      onClick={() => scrollTo(idx)}
                      className={cn(
                        'relative aspect-square h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-md border-2 bg-white transition-all duration-300 sm:h-20 sm:w-20',
                        idx === activeIndex ? 'border-brand' : 'hover:border-brand/40 border-gray-200'
                      )}>
                      <LazyImage
                        src={getResizedPictureUrl(img.file.path, 200, 200)}
                        alt={`${productTitle} thumbnail ${idx + 1}`}
                        width={200}
                        height={200}
                        sizes="80px"
                        className="h-full w-full object-contain p-1"
                      />
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {(canScrollPrevThumb || canScrollNextThumb) && (
                <>
                  {canScrollPrevThumb && (
                    <CarouselPrevious className="bg-background/90 hover:bg-brand left-1! hover:text-white" />
                  )}
                  {canScrollNextThumb && (
                    <CarouselNext className="bg-background/90 hover:bg-brand right-1! hover:text-white" />
                  )}
                </>
              )}
            </Carousel>
          </div>
        )}
      </div>
    </PhotoProvider>
  )
}

export default ProductImageGallery
